/* eslint-disable class-methods-use-this */
/* eslint-disable prettier/prettier */
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import {
    TagAction,
    BaseTagService,
    BaseVisibilityService,
    ContentService,
    Feature,
    MediaContent,
    getMetaForImage,
    getSignedFile,
    PoiType,
    DbObjectType,
    Comment,
    POI,
} from "@smarterplan/ngx-smarterplan-core";
import { Subject } from "rxjs";
import { AppConfig } from "src/config/app.config";
import { CommentType } from "../API.service";
import { AudioComponent } from "../components/audio/audio.component";
import { CarouselFullscreenComponent } from "../components/carousel-fullscreen/carousel-fullscreen.component";
import { PdfModalComponent } from "../components/pdf-modal/pdf-modal.component";
import { VideoFullscreenComponent } from "../components/video-fullscreen/video-fullscreen.component";

@Injectable({
    providedIn: "root",
})
export class MuseumTagService extends BaseTagService {
    private origin = document.location.origin;

    private widthTag = `${this.config.getEntryByKey(
        "DEFAULT_WIDTH_HTML_TAG",
    )}px`;

    public maxCharacterLength = this.config.getEntryByKey(
        "MAX_CHARACTER_DESCRIPTION_TAG",
    );

    public tagAction: Subject<{ action: string; data: any; }> = new Subject();

    public showDetailsButton = true;

    constructor(
        private translate: TranslateService,
        private config: AppConfig,
        private contentService: ContentService,
        private visibilityService: BaseVisibilityService,
        private router: Router,
    ) {
        super();
    }

    async prepareMuseumFeatureHtml(feature: Feature): Promise<string> {
        const [poi] = feature.pois.items;

        let htmlObject = `<div class="museum-main">`;
        // TITLE
        htmlObject += `<div class="museum-title">${feature.title}</div>`;
        // DESCRIPTION
        const shortDescription = this.getTagDescription(poi, feature);
        htmlObject += `<div class="museum-description">${shortDescription}</div>`;
        // READ MORE
        if (this.showDetailsButton) {
            htmlObject += `<div class="museum-read-more" id="detailBtn">${this.translate.instant(
                "Read more",
            )}</div>`;
        }
        const commentsToShow = feature.comments.items.filter(
            (com) => com.shownInTag,
        );
        // media content
        let imageAddedToTag = false;
        let videoAdded = false;
        let audioAdded = false;
        let mediaCount = 0;
        let imageHtml = "";
        let videoHtml = "";
        let audioHtml = "";
        let pdfHtml = "";
        const noVisibleContentDivHeight = 35;
        for (const comment of commentsToShow) {
            if (mediaCount === 3) {
                // console.log("No more media files allowed in tag");
                break;
            }
            if (comment.type === CommentType.IMAGE) {
                if (imageAddedToTag) {
                    // we add only one image
                    continue;
                }
                let firstImage = commentsToShow.find(
                    (comment_) =>
                        comment_.index === 1 &&
                        comment_.type === CommentType.IMAGE,
                );
                if (!firstImage) {
                    firstImage = comment;
                }
                imageAddedToTag = true;
                const image = await getMetaForImage(firstImage.annexe);
                const ratio = image.height / image.width;
                const width = Number(this.widthTag.slice(0, 3));
                const effectiveHeight = Math.ceil(width * ratio);
                const maxHeightDivImage = Math.ceil((width / 16) * 9);
                let imageStyle = `width:${this.widthTag};border-radius:8px;`;
                if (effectiveHeight > maxHeightDivImage) {
                    // image is in portrait mode, we need to recenter it
                    imageStyle += `object-fit:cover;`;
                }
                const sectionTitle = this.getFeatureSectionTitle(
                    CommentType.IMAGE,
                );
                imageHtml = `${sectionTitle} \
                <div class="museum-section-content" style="height: ${maxHeightDivImage}px; display:flex;justify-content:center; position:relative; overflow:hidden"> \
                    <img placeholder="${comment.title}" style="${imageStyle}" src="${firstImage.annexe}"> \
                    <div class="museum-btn-container museum-btn-abs" id="image-footer">
                        <img class="icon-action" src="${this.origin}/assets/icons/museum/Icon-FullScreen.svg">
                    </div>
                </div>`;
                mediaCount += 1;
            }
            if (
                comment.type === CommentType.VIDEO ||
                comment.type === CommentType.YOUTUBE
            ) {
                if (videoAdded) {
                    // we add only one video
                    continue;
                }
                const sectionTitle = this.getFeatureSectionTitle(
                    CommentType.VIDEO,
                );
                const width = Number(this.widthTag.slice(0, 3));
                const videoHeight = (width / 16) * 9;

                if (comment.type === CommentType.VIDEO) {
                    videoHtml = `${sectionTitle} \
                    <div class="museum-section-content" style="height: ${videoHeight - 10
                        }px;">
                        <video-js id="mus-video-tag" class="video-js vjs-theme-forest" controls disablePictureInPicture controlslist="nofullscreen, nodownload, " \
                        width="${this.widthTag
                        }" height="${videoHeight}px" data-setup="{}"> 
                            <source src="${comment.annexe}" type="video/mp4">
                        </video-js>
                        <div class="museum-btn-container museum-btn-abs museum-btn-sound" id="btn-mute">
                            <img class="icon-action" id="sound-on" src="${this.origin
                        }/assets/icons/museum/Icone_Son_On_white.svg">
                            <img class="icon-action" id="sound-off" src="${this.origin
                        }/assets/icons/museum/Icone_Son_Off_white.svg">
                        </div>
                        <div class="museum-btn-container museum-btn-abs" id="btn-video-fullscreen">
                            <img class="icon-action" src="${this.origin
                        }/assets/icons/museum/Icon-FullScreen.svg">
                        </div>
                    </div>`;
                }
                if (comment.type === CommentType.YOUTUBE) {
                    const previewImageUrl = `https://img.youtube.com/vi/${comment.externalLink}/0.jpg`;
                    videoHtml = `${sectionTitle} \
                    <div class="museum-section-content" style="height: ${videoHeight}px;">
                        <img placeholder="${comment.title}" style="width:${this.widthTag};border-radius:8px;object-fit:cover;" src="${previewImageUrl}">    
                        <div class="museum-btn-container museum-btn-abs" id="btn-video-fullscreen-youtube">
                            <img class="icon-action" src="${this.origin}/assets/icons/museum/Icon-playButton.svg">
                        </div>
                    </div>`;
                }
                mediaCount += 1;
                videoAdded = true;
            }
            if (comment.type === CommentType.AUDIO) {
                if (audioAdded) {
                    // we add only one audio
                    continue;
                }
                const sectionTitle = this.getFeatureSectionTitle(
                    CommentType.AUDIO,
                );

                audioHtml = `${sectionTitle}
                <div class="museum-section-content audio-player" style="height: ${noVisibleContentDivHeight}px;"> \
                    <audio id="audio-tag" width=${this.widthTag} src="${comment.annexe}"></audio>
                    <div class="audio-controls" style="width: ${this.widthTag};">
                        <div class="museum-btn-container audio-play-button">
                            <img id="play" src="${this.origin}/assets/icons/museum/play1.svg">
                            <img id="pause" src="${this.origin}/assets/icons/museum/pause.svg">
                        </div>
                        <input type="range" class="timeline-tag" max="100" value="0" min="0">
                        <div class="museum-btn-container audio-sound-btn">
                            <img class="icon-action" id="audio-sound-on" src="${this.origin}/assets/icons/museum/Icone_Son_On_white.svg">
                            <img class="icon-action" id="audio-sound-off" src="${this.origin}/assets/icons/museum/Icone_Son_Off_white.svg">
                        </div>
                        <div class="museum-btn-container audio-sound-btn" id="btn-audio-modal">
                            <img class="icon-action" src="${this.origin}/assets/icons/museum/Icon-FullScreen.svg">
                        </div>
                    </div>
                </div>`;
                mediaCount += 1;
                audioAdded = true;
            }
            if (comment.type === CommentType.DOCUMENT) {
                const sectionTitle = this.getFeatureSectionTitle(
                    CommentType.DOCUMENT,
                );
                pdfHtml = `${sectionTitle}
                <div class="museum-section-content" style="height:${noVisibleContentDivHeight}px;">
                    <div class="doc-container" id="doc-footer">
                        <img style="height:14px; margin: 0 1rem;" src="${this.origin}/assets/icons/museum/Icone-pdf-purple.svg">
                        ${comment.title}
                    </div>
                </div>`;
                mediaCount += 1;
            }
        }
        htmlObject += imageHtml + videoHtml + audioHtml + pdfHtml;
        htmlObject += "</div>";
        return htmlObject;
    }

    async getHtmlToInject(tagType, feature: Feature): Promise<string> {
        let html = "";
        if (
            feature.comments.items.find(
                (com) => com.type === CommentType.EMBED && com.shownInTag,
            )
        ) {
            // we have comment of type EMBED, return empty html since no injection, we edit Billboard
            return "";
        }
        html += await this.prepareMuseumFeatureHtml(feature);
        const styleDiv = `<head><link href="https://vjs.zencdn.net/7.17.0/video-js.css" rel="stylesheet" />
            <link href="https://unpkg.com/@videojs/themes@1/dist/forest/index.css" rel="stylesheet"/></head>
            <style> \
            @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500&display=swap');
            body {margin:0px;font-family: 'Roboto', sans-serif;}\
            .museum-main {background-color:transparent;} \
            .museum-title { color: #d8b4d4; font-size: 1.125rem; text-transform: uppercase; letter-spacing: 1px; font-weight:500;} \
            .museum-description { color: #FCE8FF; font-size: 1rem; } \
            .museum-read-more {color: #d8b4d4; cursor:pointer; text-decoration: underline; font-size: 13px; \
                border-bottom: 1px solid #68566C; padding-bottom: 0.75rem; margin-bottom: 0.75rem; margin-top: 0.5rem;}\
            .museum-section-title { color: #FCE8FF; font-size: 1rem; text-transform: uppercase; margin-bottom: 0.5rem;} \
            .museum-section-content {box-shadow: 0px 2px 8px #18061A14; border-radius: 8px; margin-bottom: 0.75rem; overflow: hidden; position:relative; display:flex;justify-content:center;} \
            .museum-btn-container {background-color: #6f3974; border-radius: 50%; display: flex; justify-content: center; \
                cursor: pointer;}
            .museum-btn-abs {z-index: 12; width: 30px; height: 30px; position: absolute; bottom: 0.5rem; right: 10px;}
            .museum-btn-sound {right: 45px;}  
            #sound-off {display: none;}     
            .icon-action {width: 20px;}
            .row-center-aligned {display: flex; align-items: center;} \
            .section-book {color: rgb(75,164,168); font-size: 1.2em; max-width: fit-content;} \
            .video-js .vjs-control-bar {width: 75%;}
            .vjs-theme-forest.vjs-big-play-button:focus, .vjs-theme-forest:hover .vjs-big-play-button { \
                background-color: transparent; \
                background-image: url('${this.origin}/assets/icons/museum/play-button-purple.svg'); \
                background-size: 100%; \
            }
            .video-js .vjs-fullscreen-control {display: none;} \
            .video-js .vjs-picture-in-picture-control {display: none;} \
            .vjs-theme-forest .vjs-volume-panel {display: none;} \
            .vjs-theme-forest .vjs-control-bar {margin: 0px; bottom: 1rem;} \
            .vjs-theme-forest .vjs-play-control .vjs-icon-placeholder:before { color: #6f3974;}
            .video-js .vjs-tech { top: -5px;}
            .vjs-theme-forest .vjs-play-control:hover .vjs-icon-placeholder:before {
                background-color:#6f3974;
                color: white;
            }
            .vjs-theme-forest .vjs-big-play-button {
                background-size: 100%;
                width: 44px;
                height: 44px;
                margin-top: -22px;
                margin-left: -22px;
            }
            .audio-controls {
                display: flex;
                flex-direction: row;
                align-items: center;
                height: 100%;
                border-radius: 27px;
                background-color: white;
            }
            .audio-play-button { margin-left: 0.5em; background-color: white !important;}
            .audio-sound-btn { width: 30px; height: 30px; margin-right: 0.4em;}
            .timeline-tag {
                -webkit-appearance: none;
                width: calc(100% - (3em + 3em + 2.7em));
                height: .5em;
                background-color: #e5e5e5;
                border-radius: 5px;
                background-size: 0% 100%;
                background-image: linear-gradient(#6f3974, #6f3974);
                background-repeat: no-repeat;
                cursor: pointer;
              }
            .timeline-tag::-webkit-slider-thumb {
                -webkit-appearance: none;
                height: 1em;
                width: 1em;
                border-radius: 50%;
                color: #6f3974; 
                background: #6f3974;
                cursor: pointer;
            }
              
            .timeline-tag::-moz-range-thumb {
                -webkit-appearance: none;
                border: none;
                border-radius: 50%;
                cursor: pointer;
                transition: all .1s;
                background-color: #6f3974;
            }
              
            .timeline-tag::-ms-thumb {
                -webkit-appearance: none;
                border: none;
                border-radius: 50%;
                cursor: pointer;
                transition: all .1s;
                background-color: #6f3974;
            }
              
            .timeline-tag::-webkit-slider-thumb:hover {background-color: #d8b4d4;}
            .timeline-tag::-moz-range-thumb:hover { background-color: #d8b4d4;}
            .timeline-tag::-ms-thumb:hover {background-color: #d8b4d4;}

            .timeline-tag::-webkit-slider-runnable-track {
                -webkit-appearance: none;
                box-shadow: none;
                border: none;
                background: transparent;
            }
              
            .timeline-tag::-moz-range-track {
                -webkit-appearance: none;
                box-shadow: none;
                border: none;
                background: transparent;
            }
              
            .timeline-tag::-ms-track {
                -webkit-appearance: none;
                box-shadow: none;
                border: none;
                background: transparent;
            }
            #pause {display:none}
            #audio-sound-off {display:none}

            .doc-container {background-color:white; height: 100%; width: 100%; border-radius:17px; display:flex; \
                color: #6f3974; font-size: 1rem; align-items: center; cursor: pointer; }
            </style>
            `;
        html += styleDiv;
        return html;
    }

    getFeatureSectionTitle(commentType: CommentType): string {
        let html = "";
        switch (commentType) {
            case CommentType.IMAGE:
                html += `<div class="row-center-aligned museum-section-title"><img style="margin-right: 0.5rem;" src="${this.origin
                    }/assets/icons/museum/Icone-Galerie-pink.svg">${this.translate.instant(
                        "Photo gallery",
                    )}</div>`;
                return html;
            case CommentType.VIDEO:
                html += `<div class="row-center-aligned museum-section-title"><img style="margin-right: 0.5rem;" src="${this.origin
                    }/assets/icons/museum/Icone-Video-pink.svg">${this.translate.instant(
                        "Video gallery",
                    )}</div> `;
                return html;
            case CommentType.AUDIO:
                html += `<div class="row-center-aligned museum-section-title"><img style="margin-right: 0.5rem;" src="${this.origin
                    }/assets/icons/museum/Icone-Audio-pink.svg">${this.translate.instant(
                        "Audio content",
                    )}</div>`;
                return html;
            case CommentType.DOCUMENT:
                html += `<div class="row-center-aligned museum-section-title"><img style="margin-right: 0.5rem;" src="${this.origin
                    }/assets/icons/museum/Icon-Download-pink.svg">${this.translate.instant(
                        "Files to Download",
                    )}</div>`;
                return html;
            default:
                return html;
        }
    }

    splitDescription(description: string): string {
        const isLong = description.length > this.maxCharacterLength;
        if (isLong) {
            const shorted = description.slice(0, this.maxCharacterLength);
            const words = shorted.split(" ");
            const wordsCount = words.length;
            words.splice(wordsCount - 1, 1);
            return `${words.join(" ")}...`;
        }
        return description;
    }

    getAnnexeForCommentTypeInFeature(
        feature: Feature,
        commentType: CommentType,
    ): string {
        const comment = feature.comments.items.find(
            (com) => com.type === commentType && com.shownInTag,
        );
        if (!comment) {
            return null;
        }
        if (commentType !== CommentType.YOUTUBE) {
            return comment.annexe;
        }
        return `https://www.youtube.com/embed/${comment.externalLink}?autoplay=1`;
    }

    getIDForCommentTypeInFeature(
        feature: Feature,
        commentType: CommentType,
    ): string {
        const comment = feature.comments.items.find(
            (com) => com.type === commentType && com.shownInTag,
        );
        return comment ? comment.id : null;
    }

    async getSignedTagIconSource(
        tagIconSource: string,
    ): Promise<string | null> {
        return getSignedFile(tagIconSource);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getScriptForTag(object, _tagType: PoiType): string {
        const videoUrl = this.getAnnexeForCommentTypeInFeature(
            object as Feature,
            CommentType.VIDEO,
        );
        const youtubeUrl = this.getAnnexeForCommentTypeInFeature(
            object as Feature,
            CommentType.YOUTUBE,
        );
        const commentAudio = object.comments?.items.find(
            (com) => com.type === CommentType.AUDIO && com.shownInTag,
        );
        const commentAudioID = commentAudio ? commentAudio.id : "";
        const detailsUrl = `museum/${object.spaceID}/feature/${object.id}`;
        const documentUrl = this.getAnnexeForCommentTypeInFeature(
            object as Feature,
            CommentType.DOCUMENT,
        );
        const showImages = object.comments?.items.some(
            (com) => com.type === CommentType.IMAGE && com.shownInTag,
        );
        let scriptTag = "<script>";
        if (videoUrl) {
            scriptTag = `
            <script src="https://vjs.zencdn.net/7.17.0/video.min.js"></script> \
            <script>
                var player;
                try {
                    player = videojs("mus-video-tag").player();
                } catch (e) {}
                var btnMute = document.getElementById("btn-mute");
                var imgOn = document.getElementById("sound-on");
                var imgOff = document.getElementById("sound-off");
                if(btnMute && player) {
                    btnMute.addEventListener("click", function () {
                        if (player.muted()) {
                            player.muted(false);
                            if (imgOn && imgOff) {
                                imgOn.style.display = "inline-block";
                                imgOff.style.display = "none";
                            }
                        } else {
                            player.muted(true);
                            if (imgOn && imgOff) {
                                imgOn.style.display = "none";
                                imgOff.style.display = "inline-block";
                            }
                        }
                    })
                }
                var fullscreenVideoBtn = document.getElementById("btn-video-fullscreen"); \
                if(fullscreenVideoBtn) {  
                    fullscreenVideoBtn.addEventListener("click", function () { window.send("videoClick", \
                "${videoUrl}");});
                } `;
        }
        if (youtubeUrl) {
            scriptTag += `
                var youtubeBtn = document.getElementById("btn-video-fullscreen-youtube"); \
                if(youtubeBtn) {
                    youtubeBtn.addEventListener("click", function () { window.send("youtubeClick", \
                "${youtubeUrl}");});
            }`;
        }
        if (commentAudioID !== "") {
            scriptTag += `
            var playerButton = document.querySelector('.audio-play-button');
                var audio = document.getElementById('audio-tag');
                var timeline = document.querySelector('.timeline-tag');
                var soundButton = document.querySelector('.audio-sound-btn');
                var playAudio = document.getElementById("play");
                var pauseAudio = document.getElementById("pause");
                var imgSoundOn = document.getElementById("audio-sound-on");
                var imgSoundOff = document.getElementById("audio-sound-off");
                var audioModal = document.getElementById("btn-audio-modal");
                if (audio && playerButton && timeline) {
                    playerButton.addEventListener('click', function () {
                        if (audio.paused) {
                            audio.play();
                            pauseAudio.style.display = "inline-block";
                            playAudio.style.display = "none";
                        } else {
                            audio.pause();
                            pauseAudio.style.display = "none";
                            playAudio.style.display = "inline-block";
                        }
                    });
                    soundButton.addEventListener('click', function () {
                        if (audio.muted) {
                            audio.muted = false;
                            imgSoundOn.style.display = "inline-block";
                            imgSoundOff.style.display = "none";
                        } else {
                            audio.muted = true;
                            imgSoundOn.style.display = "none";
                            imgSoundOff.style.display = "inline-block";
                        }
                    });
                    function changeTimelinePosition () {
                        if (audio.duration) {
                            const percentagePosition = (100*audio.currentTime) / audio.duration
                            const percentagePositionString = percentagePosition + '%';
                            timeline.style.backgroundSize =  percentagePositionString + " 100%";
                            timeline.value = percentagePosition;
                        }
                    }
                      
                    audio.ontimeupdate = changeTimelinePosition;
                    function changeSeek() {
                        const time = (timeline.value * audio.duration) / 100;
                        audio.currentTime = time;
                    }
                      
                    timeline.addEventListener('change', changeSeek);
                    timeline.addEventListener('input', changeSeek);
                    if(audioModal) {
                        audioModal.addEventListener('click', function () {
                            window.send("audioClick", "${commentAudioID}");
                        });
                    }
                }`;
        }

        if (documentUrl) {
            scriptTag += `var docContainer = document.querySelector("#doc-footer"); \
            if(docContainer) { \
                docContainer.addEventListener("click", function () { \
                    window.send("docClick", "${documentUrl}");\
                }); \
            }`;
        }
        if (showImages) {
            scriptTag += `var imgFullscreenBtn = document.getElementById("image-footer"); \
            if(imgFullscreenBtn) { \
                 imgFullscreenBtn.addEventListener("click", function () { \
                     window.send("imageClick", "${object.id}");\
                 }); \
            }`;
        }

        scriptTag += `
                var detailsLink = document.querySelector('#detailBtn'); \
                if (detailsLink) { \
                    detailsLink.addEventListener("click", function () { \
                        window.send("detailsClick", "${detailsUrl}"); \
                    }); \
                } \
            </script>`;

        return scriptTag;
    }

    onActionDetailClick(url: string): void {
        if (url !== "") {
            this.visibilityService.detailShowing.next(true);
            this.router.navigate([url]);
        }
    }

    onActionAudioClick(audioCommentID: string): void {
        // console.log("on action audio click", audioCommentID);
        if (audioCommentID) {
            this.tagAction.next({
                action: TagAction.AUDIO_CLICK,
                data: audioCommentID,
            }); // for remote session sync
            this.contentService.presentAudioModal(
                audioCommentID,
                AudioComponent,
                "modal-padding",
            );
        }
    }

    onActionVideoClick(url: string): void {
        if (url) {
            this.tagAction.next({
                action: TagAction.VIDEO_CLICK,
                data: {
                    path: url,
                    title: "",
                    description: "",
                    type: CommentType.VIDEO,
                },
            }); // for remote session sync
            this.contentService.presentVideoModal(
                {
                    path: url,
                    title: "",
                    description: "",
                    type: CommentType.VIDEO,
                } as MediaContent,
                VideoFullscreenComponent,
                "fullscreenModalClass",
            );
        }
    }

    onActionImageClick(featureID: string): void {
        // console.log("on action image click", featureID);
        this.tagAction.next({
            action: TagAction.IMAGE_CLICK,
            data: featureID,
        }); // for remote session sync
        this.contentService.presentImageCarouselModal(
            featureID,
            CarouselFullscreenComponent,
            "fullscreenModalClass",
            true,
            "0",
        );
    }

    onActionDocClick(url: string): void {
        if (url) {
            this.tagAction.next({
                action: TagAction.DOC_CLICK,
                data: url,
            }); // for remote session sync
            this.contentService.presentPdfModal(
                url,
                PdfModalComponent,
                "fullscreenModalClass",
            );
        }
    }

    getUrlForSeeDetails(object: DbObjectType, tagType: PoiType): string {
        switch (tagType) {
            case PoiType.DATA: {
                return `museum/${object.spaceID}/feature/${object.id}`;
            }
            default:
                return "";
        }
    }

    onActionYoutubeClick(url: string) {
        if (url) {
            const mediaContent = {
                path: url,
                title: "",
                description: "",
                type: CommentType.YOUTUBE,
            };
            this.tagAction.next({
                action: TagAction.YOUTUBE_CLICK,
                data: mediaContent,
            }); // for remote session sync
            this.contentService.presentVideoModal(
                mediaContent as MediaContent,
                VideoFullscreenComponent,
                "fullscreenModalClass",
            );
        }
    }

    getTagDescription(poi: POI, feature: Feature): string {
        let shortDescription = "";
        if (poi.tagDescription) {
            shortDescription = poi.tagDescription;
        } else if (feature.shortDescription) {
            shortDescription = `${this.splitDescription(
                feature.shortDescription,
            )}`;
        } else if (feature.description) {
            shortDescription = `${this.splitDescription(feature.description)}`;
        }
        return shortDescription;
    }

    getBillboardMediaToEmbed(feature: Feature): {
        comment: Comment;
        tagDescription: string;
    } {
        const commentEmbed = feature.comments.items.find(
            (com) => com.type === CommentType.EMBED && com.shownInTag,
        );
        if (commentEmbed) {
            const [poi] = feature.pois.items;
            const shortDescription = this.getTagDescription(poi, feature);
            return { comment: commentEmbed, tagDescription: shortDescription };
        }
        return { comment: null, tagDescription: null };
    }
}
